/**
 * The realtime connection.
 *
 * One EventSource for the whole app, shared by every component, because browsers
 * cap connections per origin and a dispatcher with three tabs on a busy shift
 * would otherwise exhaust them.
 *
 * Events carry ids, never data. Receiving one invalidates the relevant TanStack
 * Query keys and the normal fetch path re-reads with the user's permissions
 * applied. That is deliberate: it keeps the browser from holding patient detail
 * that never passed through the audited decrypt path, and a refetch cannot be
 * stale the way a pushed payload can.
 *
 * EventSource reconnects on its own. What it cannot do is tell you what you
 * missed while disconnected, so reconnecting triggers a full invalidation —
 * cheap, and the alternative is a board quietly showing yesterday.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@mr1/shared';

export type StreamState = 'connecting' | 'live' | 'offline';

/** Which query keys each event should refresh. */
function keysFor(event: RealtimeEvent): unknown[][] {
  switch (event.type) {
    case 'case.created':
      return [['cases']];
    case 'case.updated':
      return [['cases'], ['case', event.caseId]];
    case 'case.transitioned':
      return [['cases'], ['case', event.caseId], ['transitions', event.caseId]];
    case 'case.assigned':
      return [
        ['case', event.caseId],
        ['transitions', event.caseId],
        ['nearest', event.caseId],
        ['staff'],
        ['vehicles'],
      ];
    case 'document.added':
      return [
        ['documents', event.caseId],
        ['transitions', event.caseId],
      ];
    case 'call.ringing':
    case 'call.ended':
      return [['calls']];
    case 'call.qa_scored':
      // Prefix keys, so every open case's panel refreshes. The event names a
      // call and the panel is keyed by case; invalidating broadly is cheaper
      // than threading a case id through an event that deliberately carries ids
      // rather than data. Notably NOT ['transcript'] — that key is only ever
      // populated by a deliberate, access-logged fetch, and re-running it
      // behind someone's back would log a read they did not perform.
      return [['case-calls'], ['qa'], ['calls']];
  }
}

export interface RingingCallEvent {
  callId: string;
  from: string;
  didLabel: string | null;
  didColour: string | null;
  isEmergency: boolean;
  topMatch: string | null;
  caseId: string | null;
}

export function useEventStream(): {
  state: StreamState;
  lastEventAt: Date | null;
  ringing: RingingCallEvent[];
  dismissCall: (callId: string) => void;
} {
  const qc = useQueryClient();
  const [state, setState] = useState<StreamState>('connecting');
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);
  const [ringing, setRinging] = useState<RingingCallEvent[]>([]);
  /**
   * Dismissed calls, remembered for the life of the page.
   *
   * The PBX sends several state changes per call. Without this, dismissing the
   * banner would only hide it until the next one arrived — and a banner that
   * comes back is one people stop reading.
   */
  const dismissed = useRef<Set<string>>(new Set());
  // Ref, not state: this must not re-run the effect and tear down the stream.
  const everConnected = useRef(false);

  useEffect(() => {
    const source = new EventSource('/api/v1/events', { withCredentials: true });

    source.onopen = () => {
      setState('live');
      if (everConnected.current) {
        // A reconnect means a gap of unknown length. Everything is suspect.
        void qc.invalidateQueries();
      }
      everConnected.current = true;
    };

    source.onerror = () => {
      // EventSource retries by itself; this only reflects the state so the UI can
      // say so rather than looking live while it is not.
      setState('offline');
    };

    const onEvent = (e: MessageEvent<string>) => {
      setLastEventAt(new Date());
      let event: RealtimeEvent;
      try {
        event = JSON.parse(e.data) as RealtimeEvent;
      } catch {
        return;
      }
      for (const key of keysFor(event)) {
        void qc.invalidateQueries({ queryKey: key });
      }

      const meta = event.meta ?? {};
      if (event.type === 'call.ringing') {
        const callId = String(meta['callId'] ?? '');
        if (!callId || dismissed.current.has(callId)) return;
        setRinging((current) =>
          current.some((c) => c.callId === callId)
            ? current
            : [
                ...current,
                {
                  callId,
                  from: String(meta['from'] ?? 'withheld'),
                  didLabel: meta['didLabel'] ? String(meta['didLabel']) : null,
                  didColour: meta['didColour'] ? String(meta['didColour']) : null,
                  isEmergency: meta['isEmergency'] === true,
                  topMatch: meta['topMatch'] ? String(meta['topMatch']) : null,
                  caseId: meta['caseId'] ? String(meta['caseId']) : null,
                },
              ],
        );
      }

      if (event.type === 'call.ended') {
        const callId = String(meta['callId'] ?? '');
        setRinging((current) => current.filter((c) => c.callId !== callId));
      }
    };

    // Named events, so a future event type nobody has wired up is simply
    // ignored rather than crashing the handler.
    const types = [
      'case.created',
      'case.updated',
      'case.transitioned',
      'case.assigned',
      'document.added',
      'call.ringing',
      'call.ended',
    ];
    for (const t of types) source.addEventListener(t, onEvent as EventListener);

    return () => {
      for (const t of types) source.removeEventListener(t, onEvent as EventListener);
      source.close();
    };
  }, [qc]);

  const dismissCall = useCallback((callId: string) => {
    dismissed.current.add(callId);
    setRinging((current) => current.filter((c) => c.callId !== callId));
  }, []);

  return { state, lastEventAt, ringing, dismissCall };
}
